import React, { useMemo, useState } from 'react';
import { useStore } from '../../store/useStore';
import { UPGRADES } from '../../data/upgrades';
import * as Icons from 'lucide-react';
import type { UpgradeDef, Resource } from '../../types';

interface InternalUpgradeNodeProps {
    upgradeId: string;
    onHoverChange?: (isHovered: boolean) => void;
    isHighlighted?: boolean;
}

const UpgradeNode = ({ upgradeId, onHoverChange, isHighlighted }: InternalUpgradeNodeProps) => {
    const { upgrades, resources, purchaseUpgrade } = useStore();
    const [isHovered, setIsHovered] = useState(false);

    // Sync hover state
    const handleMouseEnter = () => {
        setIsHovered(true);
        onHoverChange?.(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        onHoverChange?.(false);
    };

    // Find upgrade definition
    const upgrade = UPGRADES.find(u => u.id === upgradeId);
    if (!upgrade) return null;

    const isPurchased = upgrades[upgrade.id];

    // Determine if unlocked (prerequisites met)
    const isUnlocked = useMemo(() => {
        if (!upgrade.prerequisites || upgrade.prerequisites.length === 0) return true;
        // MUST have all prerequisites purchased
        return upgrade.prerequisites.every(pid => upgrades[pid]);
    }, [upgrade.prerequisites, upgrades]);

    // Fog of War: Visible if Purchased OR Unlocked OR (Parent is Unlocked/Purchased??)
    // Rule: Visible if Unlocked OR if any prerequisite is visible? 
    // Stricter Rule: Visible if Unlocked. 
    // Even Stricter: Visible if Unlocked OR (a direct parent is purchased?)
    // Game styling: You see the "next" step.
    const isVisible = useMemo(() => {
        if (isPurchased) return true;
        if (isUnlocked) return true;
        // Also visible if any Prerequisite is PURCHASED (so you see the next step)
        if (upgrade.prerequisites && upgrade.prerequisites.some(pid => upgrades[pid])) return true;

        // Root nodes always visible
        if (!upgrade.prerequisites || upgrade.prerequisites.length === 0) return true;

        return false;
    }, [isPurchased, isUnlocked, upgrade.prerequisites, upgrades]);

    const canAfford = useMemo(() => {
        if (isPurchased) return false;
        if (!upgrade.cost) return true;
        for (const [res, amount] of Object.entries(upgrade.cost)) {
            if ((resources[res as Resource] ?? 0) < (amount ?? 0)) return false;
        }
        return true;
    }, [isPurchased, resources, upgrade.cost]);

    const IconComponent = (Icons as any)[upgrade.icon] || Icons.HelpCircle;

    const handleClick = () => {
        if (!isPurchased && isUnlocked && canAfford) {
            purchaseUpgrade(upgrade.id);
        }
    };

    // If strictly hidden logic was used, we returned null.
    // Now user wants "undiscovered nodes should have a circle with question mark".
    // So if "not visible" (deep in fog), we show generic node.

    // Positioning handled by parent wrapper now
    const style: React.CSSProperties = {
        width: 60,
        height: 60,
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
    };

    if (!isVisible) {
        return (
            <div style={style}>
                <div style={{
                    width: '100%', height: '100%',
                    borderRadius: '50%',
                    border: `2px dashed #444`,
                    backgroundColor: '#111',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#444',
                    cursor: 'default'
                }}>
                    <Icons.HelpCircle size={24} />
                </div>
            </div>
        );
    }

    // Visual Styles
    let borderColor = '#555';
    let bgColor = '#222';
    let iconColor = '#666';
    let shadow = 'none';
    let cursor = 'default';

    if (isPurchased) {
        borderColor = '#4caf50'; // Green
        bgColor = '#1b5e20'; // Dark Green
        iconColor = '#fff';
        shadow = '0 0 15px rgba(76, 175, 80, 0.5)';
    } else if (isUnlocked) {
        if (canAfford) {
            borderColor = '#ffd700'; // Gold
            bgColor = '#333';
            iconColor = '#fff';
            cursor = 'pointer';
            shadow = isHovered ? '0 0 10px #ffd700' : 'none';
        } else {
            borderColor = '#d32f2f'; // Red (Cant afford)
            bgColor = '#333';
            iconColor = '#aaa';
            // cursor remains default
        }
    } else {
        // Visible but Locked (e.g. parent bought but this one not unlocked?)
        // Wait, isUnlocked = checks prerequisites.
        // isVisible = checks if parent is bought.
        // If parent bought -> isVisible = true.
        // If parent bought -> isUnlocked = true (usually).
        // Unless multiple parents?
        // If node has 2 parents, and I buy 1.
        // Node is Visible (rule: any parent purchased).
        // Node is NOT Unlocked (rule: all parents purchased).
        // So this is "Locked" state. Padlock.
        borderColor = '#444';
        bgColor = '#111';
        iconColor = '#333';
    }

    // Highlight Override
    if (isHighlighted) {
        shadow = `0 0 15px 5px #00e5ff`; // Bright cyan glow
        borderColor = '#00e5ff';
    }

    return (
        <div
            style={style}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            {/* Main Node Circle */}
            <div style={{
                width: '100%', height: '100%',
                borderRadius: '50%',
                border: `3px solid ${borderColor}`,
                backgroundColor: bgColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: shadow,
                cursor: cursor,
                transition: 'all 0.2s ease-out'
            }}>
                {isUnlocked ? <IconComponent size={28} color={iconColor} /> : <Icons.Lock size={20} color="#555" />}
            </div>

            {/* Label below */}
            <div style={{
                position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)',
                width: 120, textAlign: 'center', fontSize: '0.75rem', color: '#ccc', pointerEvents: 'none',
                textShadow: '0 1px 2px black'
            }}>
                {upgrade.label}
            </div>

            {/* Tooltip on Hover */}
            {isHovered && (
                <div style={{
                    position: 'absolute', bottom: '130%', left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.95)', border: '1px solid #444', borderRadius: 4,
                    padding: 10, width: 200, zIndex: 100, pointerEvents: 'none',
                    textAlign: 'left'
                }}>
                    <div style={{ fontWeight: 'bold', color: '#fff', marginBottom: 4 }}>{upgrade.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 8 }}>{upgrade.description}</div>

                    {!isPurchased && (
                        <div style={{ fontSize: '0.8rem', color: canAfford ? '#ffd700' : '#e57373' }}>
                            Cost:
                            {Object.entries(upgrade.cost || {}).map(([r, v]) => (
                                <span key={r} style={{ marginLeft: 5 }}>{v} {r}</span>
                            ))}
                        </div>
                    )}
                    {isPurchased && <div style={{ fontSize: '0.8rem', color: '#66bb6a' }}>Purchased</div>}
                </div>
            )}
        </div>
    );
};

export default React.memo(UpgradeNode);
